/* eslint-disable no-nested-ternary */
import React, { memo, useState, useCallback } from 'react';
import { Filter } from '@saleshandy/icons';
import {
  Placement,
  TriggerType,
} from '../../../../../../shared/design-system/components/overlay/enums';
import { OverlayTooltip } from '../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../shared/utils/accessible-on-click';
import CreditsIndicator from '../../../../../../shared/components/credits-indicator';
import CallLogsSearch from './components/call-logs-search/call-logs-search';
import PurchaseCreditsModal from '../../../../../settings/components/billing-subscription/components/purchase-credits-modal';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { PurchaseEmailVerificationCreditsRequest } from '../../../../../settings/components/billing-subscription/types';
import './call-logs-sub-header.scss';
import { CallManagementState } from '../../../../../../shared/components/dialer/types';
import { GetPostLoadMetaResponse } from '../../../../../home/types';

interface IProps {
  search: string;
  onSearchChange: (value: string) => void;
  onFilterClick?: () => void;
  activeFilterCount?: number;
  currentPlanName: string;
  getEmailVerificationPlanResponse: unknown;
  sendPurchaseEmailVerificationCreditsRequestStatus: string;
  purchaseEmailVerificationCreditsRequest: (
    payload: PurchaseEmailVerificationCreditsRequest,
  ) => void;
  showAddNewTemplate?: boolean;
  handleAddNewTemplate?: () => void;
  showFilter?: boolean;
  currentCallManagementState?: CallManagementState;
  getPostLoadMetaResponse: GetPostLoadMetaResponse;
  searchPlaceholder?: string;
}

const CallLogsSubHeader: React.FC<IProps> = memo(
  ({
    search,
    onSearchChange,
    onFilterClick,
    activeFilterCount,
    currentPlanName,
    sendPurchaseEmailVerificationCreditsRequestStatus,
    purchaseEmailVerificationCreditsRequest,
    showAddNewTemplate,
    handleAddNewTemplate,
    showFilter = true,
    currentCallManagementState,
    getPostLoadMetaResponse,
    searchPlaceholder,
  }) => {
    const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = useState(
      false,
    );
    const dialerCredits =
      getPostLoadMetaResponse?.accountUsageQuotaRemaining?.['DIALER.CREDITS'];

    const dialerAddOnCredits =
      getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'DIALER.ADDON_CREDITS'
      ];

    const disableHeaderButton =
      currentCallManagementState?.callStatus === 'ringing' ||
      (currentCallManagementState?.callTwilioStatus === 'connected' &&
        currentCallManagementState?.callStatus === 'answered');

    const handleCreditsClick = useCallback(() => {
      setShowPurchaseCreditsModal(true);
    }, []);

    const handleCloseModal = useCallback(() => {
      setShowPurchaseCreditsModal(false);
    }, []);

    const handleBuyCredits = useCallback(
      (stripeCardToken: string, planId: number) => {
        purchaseEmailVerificationCreditsRequest({
          stripeCardToken,
          planId,
        });
      },
      [purchaseEmailVerificationCreditsRequest],
    );

    return (
      <>
        <div className="call-logs-sub-header">
          <div className="call-logs-sub-header__left">
            <div className="call-logs-sub-header__search">
              <CallLogsSearch
                search={search}
                onChange={onSearchChange}
                placeholder={searchPlaceholder}
              />
            </div>

            {showFilter && (
              <OverlayTooltip
                text="Filter"
                placement={Placement.Bottom}
                trigger={[TriggerType.Hover]}
              >
                <button
                  type="button"
                  className={`icon-container position-relative ${
                    disableHeaderButton ? 'disabled' : ''
                  }`}
                  disabled={disableHeaderButton}
                >
                  <div
                    className="call-logs-sub-header__icon-button-container"
                    {...accessibleOnClick(onFilterClick)}
                  >
                    <Filter
                      width={20}
                      height={20}
                      className="call-logs-sub-header__icon-button"
                    />
                  </div>
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge" />
                  )}
                </button>
              </OverlayTooltip>
            )}
          </div>

          <div className="call-logs-sub-header__right-section">
            {showAddNewTemplate === undefined && (
              <a
                href="https://docs.saleshandy.com/en/articles/14633731-how-to-purchase-and-manage-phone-numbers-in-saleshandy-dialer"
                target="_blank"
                rel="noopener noreferrer"
                className="call-logs-sub-header__credits-help-link"
              >
                How are credits deducted?
              </a>
            )}

            <button
              type="button"
              className={`call-logs-sub-header__right ${
                disableHeaderButton ? 'disabled' : ''
              }`}
              disabled={disableHeaderButton}
            >
              {showAddNewTemplate ? (
                <div
                  className="call-logs-sub-header__add-new-template"
                  {...accessibleOnClick(() => handleAddNewTemplate())}
                >
                  <span className="call-logs-sub-header__add-new-template-text">
                    Add New Template
                  </span>
                </div>
              ) : showAddNewTemplate === undefined ? (
                <CreditsIndicator
                  credits={Math.ceil(
                    Number(dialerCredits ?? 0) +
                      Number(dialerAddOnCredits ?? 0),
                  )}
                  abbreviated={true}
                  onClick={handleCreditsClick}
                />
              ) : null}
            </button>
          </div>
        </div>

        {showPurchaseCreditsModal && (
          <PurchaseCreditsModal
            show={showPurchaseCreditsModal}
            onClose={handleCloseModal}
            onBuyCredits={handleBuyCredits}
            type="dialer-credits"
            currentPlan={currentPlanName}
            // evCreditPlans={getEmailVerificationPlanResponse}
            isPurchaseEmailVerificationCreditsLoading={
              sendPurchaseEmailVerificationCreditsRequestStatus ===
              RequestStatus.Pending
            }
          />
        )}
      </>
    );
  },
);

CallLogsSubHeader.displayName = 'CallLogsSubHeader';

export default CallLogsSubHeader;
